<template>
    <div>
        <b-modal
            id="export-report-popup"
            ref="modal"
            title="Export Report"
            size="sm"
            hide-footer
            modal-class="hide-footer"
            :hide-header-close="loading"
            no-close-on-backdrop
            no-close-on-esc
        >
            <p class="m-custom">
                Would you like to download report data in Excel format or
                graphical charts as pdf?
            </p>

            <b-form
                @submit.stop.prevent="exportReport()"
                @reset.stop.prevent="onHidden"
            >
                <div class="row custom-model-form">
                    <div class="col-md-12">
                        <div class="export-radio-section">
                            <div class="icon-section">
                                <div class="excel-icons">
                                    <excel-icon></excel-icon>
                                </div>
                                <div class="pdf-icons">
                                    <pdf-icon></pdf-icon>
                                </div>
                                <div class="excel-icons">
                                    <excel-icon></excel-icon>
                                </div>
                            </div>
                            <div class="export-form-section">
                                <b-form-group class="export-files">
                                    <b-form-radio-group
                                        v-model="exportType"
                                        :options="form.exportType"
                                        name="export"
                                    ></b-form-radio-group>
                                </b-form-group>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-12 action-buttons">
                    <base-button
                        :class="loading ? 'disabled' : ''"
                        btn-label="Download"
                        btn-type="submit"
                        btn-variant="primary"
                    ></base-button>
                    <base-button
                        :disabled="loading"
                        btn-label="Cancel"
                        btn-type="reset"
                        btn-variant="link"
                    ></base-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import excelIcon from '@/components/icons/ExcelIcon.vue'
import pdfIcon from '@/components/icons/PdfIcon.vue'
import XLSX from 'xlsx'
import _ from 'lodash'
import request from '@/utils/request'
import axios from 'axios'

export default {
    /*
    |--------------------------------------------------------------------------
    | Component > components
    |--------------------------------------------------------------------------
    */
    components: {
        excelIcon,
        pdfIcon,
    },
    /*
    |--------------------------------------------------------------------------
    | Component > props
    |--------------------------------------------------------------------------
    */
    props: {
        exportGraphId: {
            type: String,
            default: null,
        },
        fileName: {
            type: String,
            required: true,
        },
        graphIds: {
            type: Array,
            required: true,
        },
    },
    /*
    |--------------------------------------------------------------------------
    | Component > data
    |--------------------------------------------------------------------------
    */
    data() {
        return {
            exportType: 'xlsx',
            form: {
                exportType: [
                    {
                        text: 'Download report data in Excel format',
                        value: 'xlsx',
                    },
                    { text: 'Download charts as PDF', value: 'pdf' },
                    {
                        text: 'Download Bulk Fetch Report',
                        value: 'bulkfetchpdf',
                    },
                ],
            },
        }
    },

    /*
    |--------------------------------------------------------------------------
    | Component > computed
    |--------------------------------------------------------------------------
    */
    computed: {
        ...mapGetters([
            'loading',
            'hidePopup',
            'chartsHeadings',
            'dataSegmentId',
            'hasScopes',
        ]),
    },
    /*
    |--------------------------------------------------------------------------
    | Component > methods
    |--------------------------------------------------------------------------
    */
    methods: {
        async exportReport() {
            try {
                this.$store.commit('setLoading', true)
                if (this.exportType == 'xlsx') {
                    this.exportExcel()
                } else if (this.exportType == 'bulkfetchpdf') {
                    this.exportBulkFetch()
                } else {
                    this.$store.commit('setLoading', false)
                    this.onHidden()
                    this.exportPdf()
                }
                if (this.hasScopes('system-logs')) {
                    await request.post('system-logs/', {
                        dataSegmentId: this.dataSegmentId,
                        module: 'insights',
                        type:
                            this.exportGraphId == 'stats-charts'
                                ? 'export-customer-statistics-' +
                                  this.exportType
                                : 'export-activity-dashboard-' +
                                  this.exportType,
                        action:
                            this.exportGraphId == 'stats-charts'
                                ? 'export-customer-statistics-' +
                                  this.exportType
                                : 'export-activity-dashboard-' +
                                  this.exportType,
                    })
                }
            } catch (error) {
                this.$store.commit('setLoading', false)
                this.$store.commit('setError', error)
            }
        }, //end of exportReport()

        exportPdf() {
            window.print()
            window.close()
        },
        exportBulkFetch() {
            axios({
                method: 'get',
                url: `${process.env.VUE_APP_API_URL}customers/generateReport2/${this.dataSegmentId}`,
                responseType: 'blob',
            })
                .then((response) => {
                    this.forceFileDownload(response)
                })
                .catch(() => console.log('error occured'))
        },

        forceFileDownload(response) {
            //console.log('This is the response:', response)

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'bulk-fetch-report.pdf')
            document.body.appendChild(link)
            link.click()
            this.$store.commit('setLoading', false)
            //console.log('Finished everyhting')
            this.onHidden()
        },

        exportExcel() {
            let headingsObj =
                this.chartsHeadings[_.camelCase(this.exportGraphId)]
            // A workbook is the name given to an Excel file
            var wb = XLSX.utils.book_new() // make Workbook of Excel
            // Define IDs of the charts we want to include in the report
            var ids = this.graphIds
            // Collect actual chart objects out of the AmCharts.charts array
            var charts = {}
            for (var i = 0; i < ids.length; i++) {
                for (var w = 0; w < window.AmCharts.charts.length; w++) {
                    if (
                        window.AmCharts.charts[w].div &&
                        window.AmCharts.charts[w].div.id &&
                        window.AmCharts.charts[w].div.id == ids[i]
                    )
                        charts[ids[i]] = window.AmCharts.charts[w]
                }
            }

            let origin = ''
            let headerOrigin = ''
            let mainHeaderOrigin = ''
            let row = 3

            /* Initial row */
            var ws = XLSX.utils.json_to_sheet([], {
                skipHeader: true,
            })

            for (var x in charts) {
                if (charts[x].dataProvider.length) {
                    let chartHeader = headingsObj[x]
                    if (this.exportGraphId == 'stats-charts') {
                        if (row == 3) {
                            origin = 'A3'
                            headerOrigin = 'A2'
                            mainHeaderOrigin = 'A1'
                            row = row + charts[x].dataProvider.length + 3
                        } else {
                            origin = 'A' + row
                            headerOrigin = 'A' + (row - 1)
                            mainHeaderOrigin = 'A' + (row - 2)
                            row = row + charts[x].dataProvider.length + 3
                        }
                        // add main header in the sheet for graph data
                        XLSX.utils.sheet_add_aoa(ws, [chartHeader.mainHeader], {
                            skipHeader: true,
                            origin: mainHeaderOrigin,
                        })
                        XLSX.utils.sheet_add_json(ws, [chartHeader.header], {
                            skipHeader: true,
                            origin: headerOrigin,
                        })

                        charts[x].dataProvider = _.filter(
                            charts[x].dataProvider,
                            function (obj) {
                                if (obj.color) {
                                    delete obj.color
                                }
                                return obj
                            }
                        )
                        XLSX.utils.sheet_add_json(ws, charts[x].dataProvider, {
                            skipHeader: true,
                            origin,
                        })
                    }

                    if (this.exportGraphId == 'activity-charts') {
                        //get sheet origin
                        if (row == 3) {
                            origin = 'A3'
                            headerOrigin = 'A2'
                            mainHeaderOrigin = 'A1'
                            row = row + charts[x].dataProvider.length + 3
                        } else {
                            origin = 'A' + row
                            headerOrigin = 'A' + (row - 1)
                            mainHeaderOrigin = 'A' + (row - 2)
                            row = row + charts[x].dataProvider.length + 3
                        }
                        XLSX.utils.sheet_add_aoa(ws, [chartHeader.mainHeader], {
                            skipHeader: true,
                            origin: mainHeaderOrigin,
                        })
                        if (x == 'screening-by-type') {
                            // add main header in the sheet for graph data

                            let formatData = []

                            for (var y in charts[x].dataProvider) {
                                if (y > 0) {
                                    origin = origin = 'A' + row
                                    headerOrigin = 'A' + (row - 1)
                                    row = row + 4
                                }
                                //add header
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [chartHeader.dataHeaders[y].header],
                                    {
                                        skipHeader: true,
                                        origin: headerOrigin,
                                    }
                                )
                                //add sub header
                                formatData[y] = {
                                    group: charts[x].dataProvider[y][
                                        'column-1'
                                    ],
                                    others: charts[x].dataProvider[y][
                                        'column-2'
                                    ],
                                }
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [
                                        chartHeader.dataHeaders[y].subHeader,
                                        formatData[y],
                                    ],
                                    {
                                        skipHeader: true,
                                        origin: origin,
                                    }
                                )
                            }
                        } else if (x == 'alerts-by-type') {
                            //add header

                            let formatData = []
                            for (var z in charts[x].dataProvider) {
                                if (z > 0) {
                                    origin = origin = 'A' + row
                                    headerOrigin = 'A' + (row - 1)
                                    row = row + 4
                                }
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [chartHeader.dataHeaders[z].header],
                                    {
                                        skipHeader: true,
                                        origin: headerOrigin,
                                    }
                                )
                                formatData[z] = {
                                    count: charts[x].dataProvider[z].count,
                                    percentage:
                                        charts[x].dataProvider[z].percentage,
                                }
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [
                                        chartHeader.dataHeaders[z].subHeader,
                                        formatData[z],
                                    ],
                                    {
                                        skipHeader: true,
                                        origin: origin,
                                    }
                                )
                            }
                        } else if (x == 'screen-hit-by-type') {
                            let formatData = []
                            for (var a in charts[x].dataProvider) {
                                if (a > 0) {
                                    origin = origin = 'A' + row
                                    headerOrigin = 'A' + (row - 1)
                                    row = row + 4
                                }
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [chartHeader.dataHeaders[a].header],
                                    {
                                        skipHeader: true,
                                        origin: headerOrigin,
                                    }
                                )
                                formatData[a] = {
                                    count: charts[x].dataProvider[a].count,
                                    percentage:
                                        charts[x].dataProvider[a].percentage,
                                }
                                XLSX.utils.sheet_add_json(
                                    ws,
                                    [
                                        chartHeader.dataHeaders[a].subHeader,
                                        formatData[a],
                                    ],
                                    {
                                        skipHeader: true,
                                        origin: origin,
                                    }
                                )
                            }
                        } else if (x == 'on-board-issue') {
                            XLSX.utils.sheet_add_json(
                                ws,
                                [chartHeader.header],
                                {
                                    skipHeader: true,
                                    origin: headerOrigin,
                                }
                            )
                            XLSX.utils.sheet_add_json(
                                ws,
                                [
                                    {
                                        with: charts[x].dataProvider[0]
                                            ? charts[x].dataProvider[0].count
                                            : 0,
                                        without: charts[x].dataProvider[1]
                                            ? charts[x].dataProvider[1].count
                                            : 0,
                                    },
                                ],
                                {
                                    skipHeader: true,
                                    origin: origin,
                                }
                            )
                        } else if (x == 'aging-by-alert-type') {
                            let formatData = []
                            XLSX.utils.sheet_add_json(
                                ws,
                                [chartHeader.header],
                                {
                                    skipHeader: true,
                                    origin: headerOrigin,
                                }
                            )
                            for (var b in charts[x].dataProvider) {
                                formatData[b] = {
                                    group: charts[x].dataProvider[b].group,
                                    manual: charts[x].dataProvider[b].manual,
                                    on_boarding:
                                        charts[x].dataProvider[b].onboarding,
                                    periodic:
                                        charts[x].dataProvider[b].periodic,
                                    triggered:
                                        charts[x].dataProvider[b].triggered,
                                }
                            }
                            XLSX.utils.sheet_add_json(ws, formatData, {
                                skipHeader: true,
                                origin: origin,
                            })
                        } else {
                            XLSX.utils.sheet_add_json(
                                ws,
                                [chartHeader.header],
                                {
                                    skipHeader: true,
                                    origin: headerOrigin,
                                }
                            )

                            XLSX.utils.sheet_add_json(
                                ws,
                                charts[x].dataProvider,
                                {
                                    skipHeader: true,
                                    origin,
                                }
                            )
                        }
                    }
                }
            }
            var allEmpty = Object.keys(charts).every(function (key) {
                return charts[key].dataProvider.length === 0
            })
            if (allEmpty) {
                XLSX.utils.sheet_add_aoa(ws, [['No Record Found']], {
                    skipHeader: true,
                    origin: 'A1',
                })
            }
            // Workbook contains one or more worksheets
            XLSX.utils.book_append_sheet(wb, ws, 'charts') // sheet Name is name of Worksheet

            // export Excel file
            XLSX.writeFile(wb, 'charts.xlsx') // name of the file is 'book.xlsx'
            this.$store.commit('setLoading', false)
            this.$store.commit(
                'setSuccess',
                'File has been downloaded successfully'
            )
            this.onHidden()
        },
        /**
         * Handle when the modal is closed
         *
         * @return {void}
         */
        onHidden() {
            this.$refs.modal.hide()
        }, // End of onHidden() method
        /**
         * Export Activity Timeline
         *
         * @return {void}
         */
        async exportActivityTimeLine() {
            try {
                // this.query.dataSegmentId = this.dataSegmentId

                let { data } = await request({
                    url:
                        'alerts/export-all-activities?dataSegmentId=' +
                        this.dataSegmentId,
                    method: 'post',
                    data: { data: this.dataSegmentId },
                })

                //console.log('data  ' + JSON.stringify(data))

                this.id = data.data.id
                this.$bvModal.show('export-process-popup')
                this.$toasted.show(data.message, {
                    type: 'success',
                })
            } catch (error) {
                this.$store.commit('setLoading', false)
                this.$store.commit('setError', error)
                // //console.log(error, 'error')
            }

            this.$store.commit('setLoading', false)
            this.onHidden()
        }, // End of onHidden() method
    },
}
</script>
